import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEnduserTimelineItems } from "@tellescope/tanstack-query";
import { useDebounce } from "@tellescope/react-hooks";
import { BlockProps } from "../types";
import { useBlock } from ".";

export interface UseAISummaryBlockArgs extends BlockProps {}

export const useAISummaryBlock = ({
  enduserId,
  info,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  viewId,
  blockId,
  blockIndex,
  blockLayout,
}: UseAISummaryBlockArgs) => {
  const { t } = useTranslation("contacts");

  const block = useBlock({
    type: "timeline.ai-summary",
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    enduserId,
    viewId,
    blockId,
    blockIndex,
    blockLayout,
  });

  const { summaryData, isLoading: isTimelineLoading } = useEnduserTimelineItems({ enduserId });

  // Local draft of the "Summary focus" field, seeded from the persisted value. Persisted to the
  // block config on a debounce while typing, and flushed immediately on blur.
  const [focus, setFocus] = useState(block.parsedInfo?.focus ?? "");

  const persistFocus = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (trimmed === (block.parsedInfo?.focus ?? "")) return;
      block.updateInfo({
        ...block.parsedInfo,
        id: block.parsedInfo?.id ?? crypto.randomUUID(),
        focus: trimmed || undefined,
      });
    },
    [block]
  );

  const { debounced: debouncedPersistFocus, flush: flushPersistFocus } = useDebounce(persistFocus, 600);

  const changeFocus = useCallback(
    (value: string) => {
      setFocus(value);
      debouncedPersistFocus(value);
    },
    [debouncedPersistFocus]
  );

  // Pass the live focus draft at call time so a just-typed focus steers this generation without
  // waiting on the persistence round-trip.
  const generateSummary = useCallback(
    () => summaryData.generateSummary({ focus: focus.trim() || undefined }),
    [summaryData, focus]
  );

  return {
    ...block,
    t,
    summary: summaryData.summary,
    summaryDate: summaryData.summaryDate,
    isGenerating: summaryData.isGenerating,
    // The summary query is disabled until timeline items load (they build its type), so treat the
    // block as loading until both the timeline and the summary lookup have settled — otherwise the
    // empty/generate state flashes during the initial fetch.
    isSummaryLoading: isTimelineLoading || summaryData.isLoading,
    isOutOfCredits: summaryData.isOutOfCredits,
    canGenerateNewSummary: summaryData.canGenerateNewSummary,
    generateSummary,
    focus,
    setFocus: changeFocus,
    saveFocus: flushPersistFocus,
  };
};
